import { Link } from "react-router-dom";
import type { Article } from "@/lib/media";
import { formatViews } from "@/lib/media";

interface ArticleRowProps {
  article: Article;
  variant?: "row" | "compact";
}

const categoryStyles: Record<string, string> = {
  新闻: "bg-primary-100 text-primary-700",
  影评: "bg-accent-100 text-accent-700",
  专题: "bg-secondary-100 text-secondary-700",
  预告: "bg-background-200 text-foreground-700",
};

export default function ArticleRow({ article, variant = "row" }: ArticleRowProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/news/detail/${article.id}`}
        className="group flex gap-3 border-b border-background-200 py-3 last:border-b-0"
      >
        <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.thumbnail}
            alt={article.title}
            title={`${article.title} 影视资讯 色虎`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-accent-600">
            {article.title}
          </h3>
          <p className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
            <span>{article.date}</span>
            <span className="h-1 w-1 rounded-full bg-background-400" />
            <span>{article.category}</span>
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/news/detail/${article.id}`}
      className="group grid grid-cols-1 gap-4 border-b border-background-200 py-5 transition-colors last:border-b-0 sm:grid-cols-[220px_1fr] md:gap-5"
    >
      <div className="aspect-[16/10] w-full overflow-hidden rounded-lg bg-background-200 sm:aspect-[16/10]">
        <img
          src={article.thumbnail}
          alt={article.title}
          title={`${article.title} 影视资讯 色虎`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>
      <div className="flex min-w-0 flex-col justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${categoryStyles[article.category]}`}>
              {article.category}
            </span>
            <span className="text-xs text-foreground-400">{article.date}</span>
            <span className="text-xs text-foreground-400">{article.author}</span>
          </div>
          <h3 className="mt-2 text-base font-semibold leading-7 text-foreground-950 transition-colors group-hover:text-accent-600 md:text-lg">
            {article.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-foreground-500">{article.summary}</p>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true" />
            {formatViews(article.views)}
          </span>
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="tag-chip bg-background-100">
              {tag}
            </span>
          ))}
          <span className="ml-auto flex items-center gap-1 font-medium text-primary-700">
            阅读全文
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}